import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Loader2, Lock, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email, password });
      const { access_token, user } = res.data;
      const ADMIN_ROLES = ['super_admin', 'admin', 'accountant', 'support_agent', 'content_manager', 'personal_assistant'];
      if (!ADMIN_ROLES.includes(user.role)) {
        setError('Access denied. Admin credentials required.');
        return;
      }
      localStorage.setItem('sad_token', access_token);
      localStorage.setItem('sad_user', JSON.stringify(user));
      navigate('/sad/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-4" data-testid="sad-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">VendFactura</h1>
          <p className="text-slate-400 text-sm mt-1">Super Admin Console</p>
        </div>

        <form onSubmit={handleLogin} className="bg-[#1e293b] border border-slate-700 p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 p-3 text-red-400 text-sm" data-testid="sad-login-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-2.5 bg-[#0f172a] border border-slate-600 text-white text-sm rounded-sm focus:border-amber-500 focus:outline-none"
                placeholder="admin@vendfactura.com"
                data-testid="sad-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-2.5 bg-[#0f172a] border border-slate-600 text-white text-sm rounded-sm focus:border-amber-500 focus:outline-none"
                placeholder="********"
                data-testid="sad-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white py-2.5 font-semibold hover:from-amber-600 hover:to-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="sad-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Access Console
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default SuperAdminLogin;
